import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import API from "../api/api";
import "./ManageSlider.css";

export default function MostlyUsedAdmin() {
  const [links, setLinks] = useState([]);
  const [orderedList, setOrderedList] = useState([]);
  const [logo, setLogo] = useState(null);
  const [customLink, setCustomLink] = useState("");

  // Dropdown states
  const [schemes, setSchemes] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [selectedMain, setSelectedMain] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState("");

  const [editId, setEditId] = useState(null);

  // Load Schemes
  const loadSchemes = async () => {
    try {
      const res = await API.get("/schemes");
      setSchemes(res.data);

      const mains = [...new Set(res.data.map((s) => s.mainCategory))];
      setMainCategories(mains);
    } catch (err) {
      console.error("Error loading schemes", err);
    }
  };

  // Load Most Used Links
  const loadLinks = async () => {
    try {
      const res = await API.get("/mostly-used/all");

      const sorted = res.data.data.sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );

      setLinks(sorted);
      setOrderedList(sorted);
    } catch (err) {
      Swal.fire("Error", "Failed to load links", "error");
    }
  };

  useEffect(() => {
    loadSchemes();
    loadLinks();
  }, []);

  // MAIN CATEGORY CHANGE
  const handleMainChange = (main) => {
    setSelectedMain(main);
    setSelectedCat("");
    setCategories([]);
    setCustomLink("");

    const found = schemes.find((s) => s.mainCategory === main);
    if (found) setCategories(found.categories || []);
  };

  // CATEGORY CHANGE
  const handleCategoryChange = (catName) => {
    setSelectedCat(catName);
    setCustomLink("");

    const url = `/category/${encodeURIComponent(catName)}`;
    setCustomLink(url);
  };

  // MOVE ITEM ORDER
  const moveItem = (index, direction) => {
    const updated = [...orderedList];

    if (direction === "up" && index > 0) {
      [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
    }

    if (direction === "down" && index < updated.length - 1) {
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    }

    const reordered = updated.map((item, i) => ({
      ...item,
      order: i + 1,
    }));

    setOrderedList(reordered);
  };

  // SAVE ORDER
  const saveOrder = async () => {
    const confirm = await Swal.fire({
      title: "Save Order?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Save",
    });

    if (!confirm.isConfirmed) return;

    try {
      await API.put("/mostly-used/reorder", { items: orderedList });

      Swal.fire({
        icon: "success",
        title: "Order Saved",
        timer: 1200,
        showConfirmButton: false,
      });

      loadLinks();
    } catch (err) {
      Swal.fire("Error", "Failed to save order", "error");
    }
  };

  // ADD OR UPDATE LINK
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!logo && !editId) {
      Swal.fire("Missing Logo", "Please upload a logo image.", "warning");
      return;
    }

    if (!customLink) {
      Swal.fire("Missing Link", "Please select category or enter a link.", "warning");
      return;
    }

    const formData = new FormData();
    if (logo) formData.append("logo", logo);
    formData.append("link", customLink);

    const isUpdate = !!editId;

    const confirm = await Swal.fire({
      title: isUpdate ? "Update Link?" : "Add New Link?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isUpdate ? "Update" : "Add",
    });

    if (!confirm.isConfirmed) return;

    try {
      if (isUpdate) {
        await API.put(`/mostly-used/update/${editId}`, formData);
      } else {
        await API.post("/mostly-used/add", formData);
      }

      Swal.fire({
        icon: "success",
        title: isUpdate ? "Updated!" : "Added!",
        timer: 1500,
        showConfirmButton: false,
      });

      setLogo(null);
      setEditId(null);
      setCustomLink("");
      setSelectedMain("");
      setSelectedCat("");

      loadLinks();

    } catch (err) {
      Swal.fire("Error", "Failed to save link", "error");
    }
  };

  // DELETE
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete this logo?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!confirm.isConfirmed) return;

    try {
      await API.delete(`/mostly-used/${id}`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        timer: 1200,
        showConfirmButton: false,
      });

      loadLinks();

    } catch (err) {
      Swal.fire("Error", "Delete failed", "error");
    }
  };

  return (
    <div className="slider-wrapper">
      <div className="slider-container shadow-lg">

        <h3 className="slider-title">Manage Mostly Used Links</h3>
        <div className="underline"></div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="upload-box">

          <input
            type="file"
            className="form-control upload-input"
            onChange={(e) => setLogo(e.target.files[0])}
            required={!editId}
          />

          {/* Main Category */}
          <select
            className="form-select mt-2"
            value={selectedMain}
            onChange={(e) => handleMainChange(e.target.value)}
          >
            <option value="">Select Service Group</option>
            {mainCategories.map((mc, i) => (
              <option key={i} value={mc}>
                {mc}
              </option>
            ))}
          </select>

          {/* Category */}
          {categories.length > 0 && (
            <select
              className="form-select mt-2"
              value={selectedCat}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="">Select Service Type</option>
              {categories.map((cat, i) => (
                <option key={i} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          )}

          {/* Manual Link */}
          <input
            type="text"
            className="form-control mt-2"
            placeholder="Or enter a custom link"
            value={customLink}
            onChange={(e) => setCustomLink(e.target.value)}
          />

          <button type="submit" className="btn upload-btn mt-3">
            {editId ? "Update Link" : "Add Link"}
          </button>

        </form>

        {/* LIST */}
        <div className="slider-grid mt-4">

          {orderedList.map((item, index) => (
            <div className="slider-grid-item" key={item._id}>
              <div className="image-card">

                <img src={item.logo} className="slider-img" alt="logo" />

                <div className="text-center mt-1">
                  <small className="text-primary">
                    <u>{item.link}</u>
                  </small>
                </div>

                <div className="image-controls">

                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => moveItem(index, "up")}
                    disabled={index === 0}
                  >
                    ↑
                  </button>

                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => moveItem(index, "down")}
                    disabled={index === orderedList.length - 1}
                  >
                    ↓
                  </button>

                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => {
                      setEditId(item._id);
                      setCustomLink(item.link);
                    }}
                  >
                    ✏️
                  </button>

                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(item._id)}
                  >
                    🗑
                  </button>

                </div>

                <div className="text-center mt-2">
                  <small>Position: {index + 1}</small>
                </div>

              </div>
            </div>
          ))}

        </div>

        {orderedList.length > 1 && (
          <div className="text-center mt-4">
            <button className="btn save-btn" onClick={saveOrder}>
              💾 Save Order
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
